import {
  signupSchema,
  type VerificationRequired,
} from "@reelio/contracts";
import { Check } from "lucide-react";
import { useState, type FormEvent } from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { authClient } from "./auth-client";
import { getAuthErrorMessage } from "./auth-errors";
import { useAuth } from "./AuthProvider";
import { AuthShell } from "./AuthShell";
import { Field, PrimaryButton } from "./FormControls";
import { getSafeRedirect } from "./redirect";

type SignupDraft = {
  email: string;
  businessName: string;
  instagramHandle: string;
  password: string;
};

type FieldErrors = Partial<Record<keyof SignupDraft, string>>;

const firstError = (
  errors: Record<string, string[] | undefined>,
  field: keyof SignupDraft,
) => errors[field]?.[0];

export const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const [draft, setDraft] = useState<SignupDraft>({
    email: "",
    businessName: "",
    instagramHandle: "",
    password: "",
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pending, setPending] = useState<VerificationRequired | null>(null);
  const [resendMessage, setResendMessage] = useState("");
  const loginPath = `/auth/login${location.search}`;

  const updateDraft = (field: keyof SignupDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setFormError("");
  };

  const completeSignup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = signupSchema.safeParse(draft);

    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      setErrors({
        email: firstError(fieldErrors, "email"),
        businessName: firstError(fieldErrors, "businessName"),
        instagramHandle: firstError(fieldErrors, "instagramHandle"),
        password: firstError(fieldErrors, "password"),
      });
      return;
    }

    setIsSubmitting(true);
    setFormError("");

    try {
      const result = await authClient.signUp.email({
        name: parsed.data.businessName,
        email: parsed.data.email,
        password: parsed.data.password,
        businessName: parsed.data.businessName,
        instagramHandle: parsed.data.instagramHandle,
      });

      if (result.error) {
        setFormError(
          getAuthErrorMessage(
            result.error,
            "Reelio could not create the account. Try again.",
          ),
        );
        return;
      }

      const viewer = await refresh();
      if (!viewer) {
        setPending({
          requiresEmailVerification: true,
          email: parsed.data.email,
          message: "Check your email to verify your account.",
        });
        return;
      }

      navigate(getSafeRedirect(location.search), { replace: true });
    } catch (error) {
      setFormError("Reelio could not create the account. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resend = async () => {
    if (!pending) return;
    setResendMessage("");
    try {
      const result = await authClient.sendVerificationEmail({
        email: pending.email,
        callbackURL: getSafeRedirect(location.search),
      });
      setResendMessage(
        result.error
          ? getAuthErrorMessage(
              result.error,
              "We could not resend the verification email.",
            )
          : "A new verification email is on its way.",
      );
    } catch (error) {
      setResendMessage("We could not resend the verification email.");
    }
  };

  return (
    <AuthShell
      title={pending ? "Check your email" : "Create an account"}
      description={
        pending
          ? `We sent a verification link to ${pending.email}.`
          : "Create your workspace to turn Shopify products into a month of Instagram content."
      }
      footer={
        pending ? null : (
          <p>
            Already have an account? <Link to={loginPath}>Log in</Link>
          </p>
        )
      }
    >
      {pending ? (
        <section className="auth-success" aria-live="polite">
          <span className="auth-success__icon" aria-hidden="true">
            <Check size={20} strokeWidth={2} />
          </span>
          <h2>Verify your email to continue.</h2>
          <p>The link expires in 24 hours.</p>
          {resendMessage ? <p>{resendMessage}</p> : null}
          <button className="auth-secondary" type="button" onClick={resend}>
            Resend verification email
          </button>
        </section>
      ) : (
        <form className="auth-form" onSubmit={completeSignup} noValidate>
          <Field
            label="Business name"
            name="businessName"
            autoComplete="organization"
            value={draft.businessName}
            error={errors.businessName}
            onChange={(event) =>
              updateDraft("businessName", event.target.value)
            }
          />

          <Field
            label="Work email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={draft.email}
            error={errors.email}
            onChange={(event) => updateDraft("email", event.target.value)}
          />

          <Field
            label="Instagram handle"
            name="instagramHandle"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={draft.instagramHandle}
            error={errors.instagramHandle}
            onChange={(event) =>
              updateDraft("instagramHandle", event.target.value)
            }
          />

          <Field
            label="Password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={draft.password}
            error={errors.password}
            onChange={(event) => updateDraft("password", event.target.value)}
            hint="10+ characters"
          />

          {formError ? (
            <p className="auth-form__alert" role="alert">
              {formError}
            </p>
          ) : null}

          <PrimaryButton
            type="submit"
            isLoading={isSubmitting}
            loadingLabel="Creating workspace…"
          >
            Create workspace
          </PrimaryButton>
        </form>
      )}
    </AuthShell>
  );
};
